import React, { useState, useEffect, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import UpdateTipoCirurgiaDrawer from "../../components/Modals/UpdateTipoCirurgiaDrawer/index.tsx";
import CreateTipoCirurgiaDrawer from "../../components/Modals/CreateTipoCirurgiaDrawer/index.tsx";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import { Response } from "../../types/Api.ts";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import "../../styles/design-system.css";

interface TipoCirurgia {
  id: string;
  nome: string;
  observacao?: string;
}

const ListagemTipoCirurgia = () => {
  const [tiposCirurgia, setTiposCirurgia] = useState<TipoCirurgia[]>([]);
  const [busca, setBusca] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentTipo, setCurrentTipo] = useState<TipoCirurgia | null>(null);
  const [tipoParaExcluir, setTipoParaExcluir] = useState<TipoCirurgia | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcluindo, setIsExcluindo] = useState(false);

  useEffect(() => {
    fetchTiposCirurgia();
  }, []);

  const fetchTiposCirurgia = async () => {
    try {
      setIsLoading(true);
      const response = await handleService({ endpoint: "/tipo-cirurgia" });
      const lista: TipoCirurgia[] = Array.isArray(response) ? response : response.data || [];
      setTiposCirurgia(lista);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditar = (tipo: TipoCirurgia) => {
    setCurrentTipo(tipo);
    setIsEditOpen(true);
  };

  const handleVisualizar = (tipo: TipoCirurgia) => {
    setCurrentTipo(tipo);
    setIsViewOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setCurrentTipo(null);
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setCurrentTipo(null);
  };

  const onUpdate = (response: Response<TipoCirurgia>) => {
    const tipo = response.data;
    if (tipo) {
      setTiposCirurgia((prev) => prev.map((t) => (t.id === tipo.id ? tipo : t)));
      setIsEditOpen(false);
    }
  };

  const onCreated = (response: Response<TipoCirurgia>) => {
    const tipo = response.data;
    if (tipo) {
      setTiposCirurgia((prev) => [...prev, tipo]);
      setIsCreateOpen(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!tipoParaExcluir) return;
    try {
      setIsExcluindo(true);
      await handleService({ endpoint: `/tipo-cirurgia/${tipoParaExcluir.id}`, method: MethodsEnum.DELETE });
      setTiposCirurgia((prev) => prev.filter((t) => t.id !== tipoParaExcluir.id));
      setTipoParaExcluir(null);
    } catch (error) {
    } finally {
      setIsExcluindo(false);
    }
  };

  const filtrados = useMemo(
    () => tiposCirurgia.filter((t) => t.nome?.toLowerCase().includes(busca.toLowerCase())),
    [tiposCirurgia, busca]
  );

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Tipos de cirurgia</h1>
          <p>Visualize e gerencie os tipos de cirurgia da sua clínica.</p>
        </div>
        <div className="ds-page-head-actions">
          <Button variant="primary" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setIsCreateOpen(true)}>
            Novo tipo
          </Button>
        </div>
      </div>

      <div className="ds-toolbar">
        <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome..." />
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Observação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={3} />
            ) : filtrados.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  <EmptyState title="Nenhum tipo de cirurgia encontrado" description="Cadastre o primeiro tipo de cirurgia para começar." actionLabel="Novo tipo" onAction={() => setIsCreateOpen(true)} />
                </td>
              </tr>
            ) : (
              filtrados.map((tipo) => (
                <tr key={tipo.id}>
                  <td className="ds-cell-title">{tipo.nome}</td>
                  <td>{tipo.observacao || "—"}</td>
                  <td>
                    <div className="ds-row-actions">
                      <button className="ds-icon-btn" onClick={() => handleVisualizar(tipo)} title="Visualizar">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => handleEditar(tipo)} title="Editar">
                        <EditOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => setTipoParaExcluir(tipo)} title="Excluir">
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UpdateTipoCirurgiaDrawer isOpen={isEditOpen} onClose={handleCloseEdit} tipoCirurgia={currentTipo} onUpdate={onUpdate} />
      <UpdateTipoCirurgiaDrawer isOpen={isViewOpen} onClose={handleCloseView} tipoCirurgia={currentTipo} readOnly />
      <CreateTipoCirurgiaDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onCreated} />

      <ConfirmDeleteDialog
        open={!!tipoParaExcluir}
        title="Excluir Tipo de Cirurgia"
        message="Tem certeza que deseja excluir este tipo de cirurgia cadastrado? Esta ação não poderá ser desfeita."
        loading={isExcluindo}
        onCancel={() => setTipoParaExcluir(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
};

export default ListagemTipoCirurgia;
